import moment from 'moment';

// Rental equipment billed on HeavyJob usage — hours × the rate sheet rate,
// the same basis owned fleet (52100) bills on. The rate sheet bills its rate
// "regardless of how contractor chooses to source such equipment", so a
// rented crane bills the crane's sheet rate, not the vendor's invoice plus a
// markup.
//
// JDE has no rental usage: a 52200 row is an AP voucher line whose
// Total_Hours is the voucher QUANTITY (a month of crane posts as "1"). The
// usage lives in HeavyJob, where a rental unit is coded PO-<po>-<line>
// (readRentalUsage): one usage line per unit × work date × cost code, off the
// job's own cards — a job-70 PO's unit working a job-71 card bills on 71,
// where that ticket was signed. Its tier comes off the JDE PO line (PDFRAT),
// which is what the Rates & Rules tier map prices.
//
// Usage lines carry no cost (the cost is the vendor's voucher) and are keyed
// ru_<unit>|<date>|<cost code>, so a line keeps its identity — and its frozen
// summary snapshot — when the card's hours are edited. A voucher on a PO line
// whose unit has HeavyJob hours — on these cards or another job's — stays on
// the job as cost but defaults Not Billable ("bills on HeavyJob usage"); a
// billable override puts one back at cost + Equipment %. The PO's other
// lines (freight, maintenance, damage waiver — JDE stamps the tier on every
// line of a rental PO) and units never set up in HeavyJob keep billing on
// their own rules.
//
// settings.ru — when rentals switched to this basis:
//   ''          auto: on once the job's cards carry rental units (or a
//               usage line is already on a summary). Jobs that billed rental
//               vouchers at cost + % start the day after the latest work
//               date on any summary, so a period already billed on the
//               vouchers doesn't bill again on hours.
//   'YYYY-MM-DD' on from that work date — usage before it is left out and
//               vouchers dated before it keep billing cost + %.
//   'off'       vouchers bill cost + Equipment %, no usage lines.

export const RU_PREFIX = 'ru_';
export const RENTAL_CT = '52200';
// A stable empty `elsewhere` for callers that haven't loaded yet (memo deps).
export const NO_RENT_ELSEWHERE = Object.freeze([]);

/** A rental usage line — live (priceCostRows' flag) or off a summary snapshot's key. */
export const isRentUsage = (t) => Boolean(t && (t.rentUsage || String(t.key || '').startsWith(RU_PREFIX)));

export const rentUsageKey = (code, date, cc) => `${RU_PREFIX}${code}|${date}|${cc}`;

const unpad = (po) => String(po ?? '').trim().replace(/^0+/, '');
const num = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};
const isYmd = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || ''));

/** The HeavyJob code of a JDE rental PO line (PO 00231534 line 1 → PO-231534-1). */
export const rentUnitCode = (po, pol) => (unpad(po) && num(pol) ? `PO-${unpad(po)}-${num(pol)}` : '');

// Title-case a JDE item ("120-TON RT CRANE" → "120-Ton RT Crane"), short
// words kept upper — same rule as the ticket scripts' rentalLabel().
const titleItem = (s) => String(s || '').trim().toLowerCase()
    .replace(/[a-z]+/g, (w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)));

/** "<Item> - <Description 1>", or the description alone when it already names the item. */
export const rentUnitLabel = (item, desc) => {
    const d = String(desc || '').trim();
    const i = String(item || '').trim();
    if (!i) return d;
    if (!d || d.toLowerCase().includes(i.toLowerCase())) return d || titleItem(i);
    return `${titleItem(i)} - ${d}`;
};

/**
 * readRentalUsage's response → usage rows, or null when it didn't load (a
 * function not deployed yet, a failed read) — null keeps the job on the
 * voucher basis rather than zeroing its rentals with nothing to replace them.
 */
export const rentalUsageOf = (res) => {
    if (!res || res.error || !Array.isArray(res.data)) return null;
    return res.data
        .map((r) => ({
            code: String(r.code || '').trim().toUpperCase(),
            po: unpad(r.po),
            ln: num(r.ln),
            date: String(r.date || '').slice(0, 10),
            cc: String(r.cc || '').trim(),
            ccDesc: String(r.ccDesc || '').trim(),
            hrs: num(r.hrs),
            desc: String(r.desc || '').trim(),
            item: String(r.item || '').trim(),
            tier: String(r.tier || '').trim(),
            poJob: String(r.poJob || '').trim(),
            vendor: String(r.vendor || '').trim(),
        }))
        .filter((r) => r.code && isYmd(r.date) && r.hrs);
};

/**
 * readRentalUsage's `elsewhere` → [{ code, job, hrs }]: this job's rental
 * units with hours on other jobs' cards. They bill there, so their vouchers
 * here stay off the invoice. [] when it didn't load.
 */
export const rentalElsewhereOf = (res) => (res && Array.isArray(res.elsewhere) ? res.elsewhere : [])
    .map((r) => ({ code: String(r.code || '').trim().toUpperCase(), job: String(r.job || '').trim(), hrs: num(r.hrs) }))
    .filter((r) => r.code && r.hrs);

/** The job's rental (52200) POs off its cost history, unpadded and sorted — readRentalUsage's `pos`. */
export const rentalPosOf = (jobCostDetail) => [...new Set((jobCostDetail || [])
    .filter((r) => String(r?.Cost_Type_Code || '') === RENTAL_CT)
    .map((r) => unpad(r.PO_Number))
    .filter((p) => /^\d+$/.test(p)))].sort();

/**
 * Is the job billing rentals on usage, and from what work date?
 * → { on, from, auto } — `from` '' means every usage day bills.
 */
export const rentUsageFrom = (settings, billed, usage) => {
    const s = String((settings || {}).ru || '').trim();
    if (s === 'off') return { on: false, from: '', auto: false };
    const snaps = Object.entries(billed || {});
    const billedUsage = snaps.some(([k]) => k.startsWith(RU_PREFIX));
    const on = billedUsage || (Array.isArray(usage) && usage.length > 0);
    if (isYmd(s)) return { on, from: s, auto: false };
    // Auto: a job that billed rental vouchers at cost + % already billed
    // its rentals through the last summarized day.
    const billedVouchers = snaps.some(([k, rec]) => !k.startsWith(RU_PREFIX) && String(rec?.snap?.ct || '') === RENTAL_CT);
    if (!billedVouchers) return { on, from: '', auto: true };
    const last = snaps.reduce((m, [, rec]) => {
        const d = String(rec?.snap?.d || '').slice(0, 10);
        return isYmd(d) && d > m ? d : m;
    }, '');
    return { on, from: last ? moment.utc(last).add(1, 'day').format('YYYY-MM-DD') : '', auto: true };
};

/**
 * Usage rows → cost-history-shaped rows for priceCostRows (flagged `_ru`):
 * the days on or after `from`, plus any usage line already on a summary —
 * a billed line never drops off, even if its card changed or the usage
 * didn't load (it's rebuilt off its snapshot). `poText` maps an unpadded PO
 * to the job's own cost-history spelling so usage groups with the
 * vouchers of the same PO line.
 */
export const rentUsageCostRows = (usage, { from = '', billed = {}, poText = {} } = {}) => {
    const out = [];
    const seen = new Set();
    (usage || []).forEach((u) => {
        const key = rentUsageKey(u.code, u.date, u.cc);
        if (seen.has(key)) return;
        if (from && u.date < from && !billed[key]) return;
        seen.add(key);
        out.push({
            _ru: { key, poJob: u.poJob },
            Cost_Type_Code: RENTAL_CT,
            Cost_Type_Code_Description: 'Rental usage (HeavyJob)',
            Phase_Code: u.cc,
            Phase_Code_Description: u.ccDesc,
            Service_Date: u.date,
            Total_Hours: u.hrs,
            Job_Cost: 0,
            PO_Number: poText[u.po] || u.po,
            Line_ID: u.ln,
            Unit_Number: u.code,
            Rental_Equipment_Classification_Tier_Name: u.tier,
            Remark_Description: rentUnitLabel(u.item, u.desc),
            Supplier_Name: u.vendor,
        });
    });
    Object.entries(billed || {}).forEach(([key, rec]) => {
        if (!key.startsWith(RU_PREFIX) || seen.has(key)) return;
        const m = /^ru_(PO-(\d+)-(\d+))\|(\d{4}-\d{2}-\d{2})\|(.*)$/.exec(key);
        const snap = rec?.snap || {};
        if (!m) return;
        seen.add(key);
        out.push({
            _ru: { key, poJob: '' },
            Cost_Type_Code: RENTAL_CT,
            Cost_Type_Code_Description: 'Rental usage (HeavyJob)',
            Phase_Code: m[5],
            Service_Date: m[4],
            Total_Hours: num(snap.hr),
            Job_Cost: 0,
            PO_Number: poText[m[2]] || m[2],
            Line_ID: num(m[3]),
            Unit_Number: m[1],
            Remark_Description: String(snap.ds || ''),
            Supplier_Name: String(snap.who || ''),
        });
    });
    return out;
};

/** Distinct tiers on the usage, with their hours — for the tier map. */
export const rentUsageTiers = (usage) => {
    const m = new Map();
    (usage || []).forEach((u) => {
        if (!u.tier) return;
        const e = m.get(u.tier) || { code: u.tier, hrs: 0 };
        e.hrs += u.hrs;
        m.set(u.tier, e);
    });
    return [...m.values()];
};
